import config from '@/config/config';
import createRestClient from '../restClient';

import { type Document } from '~/common/types/document/document';
import { type TemporaryFile } from '@/containers/Cases/Repair/Timeline/Steps/SubmitOfferOrInvoice/UploadFileModal';
import { type TemporaryFileResponse } from '@/mutations/temporaryFiles/useUploadTemporaryFile';

const restClient = createRestClient();
const client = createRestClient(config.baseURL);

type UploadOptions = {
  onProgress?: (progress: number) => void;
};

const putUpdateFile = ({
  fileId,
  data,
}: {
  fileId: string;
  data: { fileName: string; type: string };
}) => restClient.put(`file/${fileId}`, data);

const putUpdateFilesType = (data: { fileIds: string[]; type: string }) =>
  restClient.put('file/type', data);

const downloadFile = (fileId: string) => restClient.get(`file/${fileId}/download`);

const getFileInfo = (fileId: string): Promise<Document> => client.get(`file/${fileId}/info`);

const putBulkUpdateFileAccessibility = (data: {
  caseId?: string;
  fileIds: string[];
  isPublic: boolean;
  teamsWithAccess: string[];
}) => restClient.put('file/accessibility', data);

const getUriForFile: (id: string) => Promise<string> = (id) => client.get(`file/${id}/fileUri`);

const uploadTemporaryFiles = ({
  data,
  onProgress,
}: {
  data: FormData;
  onProgress: (progress: number) => void;
}) =>
  restClient.post<TemporaryFile[]>('/temporaryfiles/multiple', data, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (progressEvent) => {
      if (progressEvent && progressEvent.loaded && progressEvent.total) {
        const progress = (progressEvent.loaded / progressEvent.total) * 100;

        onProgress(Math.round(progress));
      }
    },
  });

const STREAM_UPLOAD_MAX_PROGRESS = 95;

const uploadTemporaryFileStream = async (
  file: File,
  { onProgress }: UploadOptions = {},
): Promise<TemporaryFileResponse> => {
  const response = await restClient.post(
    `temporaryFiles/stream?fileName=${encodeURIComponent(file.name)}`,
    file,
    {
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent?.loaded && progressEvent?.total) {
          const uploadProgress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          onProgress?.(Math.min(STREAM_UPLOAD_MAX_PROGRESS, uploadProgress));
        }
      },
    },
  );

  onProgress?.(100);

  return response as unknown as TemporaryFileResponse;
};

const uploadTemporaryFileStreams = async (
  files: File[],
  { onProgress }: UploadOptions = {},
): Promise<TemporaryFileResponse[]> => {
  const promises: Promise<TemporaryFileResponse>[] = [];

  for (let index = 0; index < files.length; index += 1) {
    const file = files[index];

    promises.push(uploadTemporaryFileStream(file, {
      onProgress: (fileProgress) => {
        const overallProgress = ((index + fileProgress / 100) / files.length) * 100;
        onProgress?.(Math.round(overallProgress));
      },
    }));
  }

  return Promise.all(promises);
};

export default {
  downloadFile,
  getFileInfo,
  getUriForFile,
  putBulkUpdateFileAccessibility,
  putUpdateFile,
  putUpdateFilesType,
  uploadTemporaryFiles,
  uploadTemporaryFileStream,
  uploadTemporaryFileStreams,
};
